import { type FC, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import RegisterLayout from '@/registration/component/Register/Layout';
import { Center, SkeletonCircle, SkeletonText } from '@chakra-ui/react';
import { UiButton, UiHStack, UiSimpleGrid, UiStack, uiStyles, UiText } from '@/lib/ui';
import { useUserSession } from '@/registration/hook/useUserSession';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { type UseQueryResult } from '@tanstack/react-query';
import { type Registration } from '@/api/registration';
import { type GroupRegistration } from '@/api/registration/groupRegistration';

interface RouteGuardProps {
  registrationQuery: UseQueryResult<Registration, Error>
  groupRegistrationQuery: UseQueryResult<GroupRegistration, Error>
}

const PrepareDetails: FC<RouteGuardProps> = ({
  registrationQuery,
  groupRegistrationQuery
}) => {
  const { eventId, tenantCode } = useRegisterRoute();
  const location = useLocation();
  const navigate = useNavigate();
  const { userSession } = useUserSession();

  /**
   * @todo We don't recommend to write inline functions. It's hard to read and change (e.g. if we need to add more lines in the function body).
   */
  const emailPageUrl = useMemo(
    () => {
      return generatePageUrl('RegistrationRegisterEmail', { tenantCode, eventId });
    },
    [eventId, tenantCode]
  );

  const confirmationUrl = useMemo(
    () => {
      return generatePageUrl('RegistrationRegisterConfirmation', { tenantCode, eventId });
    }
    , [eventId, tenantCode]);

  const onBackEmail = useCallback(() => { return navigate(emailPageUrl); }, [emailPageUrl, navigate]);

  if ((!(userSession ?? registrationQuery?.error) || (groupRegistrationQuery?.error)) && ![emailPageUrl, confirmationUrl].includes(location.pathname)) {
    return (
      <RegisterLayout showShoppingCart={false}>
        <BaseMessageBarError borderRadius={uiStyles.borderRadius}>You are no longer signed in.</BaseMessageBarError>
        <UiHStack justifyContent={'flex-end'}>
          <UiButton colorScheme={'primary'} variant={'ghost'} p={0} mt={8} onClick={onBackEmail}>
            Back to the email page
          </UiButton>
        </UiHStack>
      </RegisterLayout>
    );
  }

  if ((registrationQuery?.data && registrationQuery?.isLoading) || (groupRegistrationQuery?.data && groupRegistrationQuery?.isLoading)) { // loading registration
    return (
      <RegisterLayout showShoppingCart={false}>
        <SkeletonCircle size='16' />
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }

  if ((registrationQuery?.isLoading || groupRegistrationQuery?.isLoading)) { // loading registration
    return (
      <RegisterLayout showShoppingCart={false}>
        <SkeletonCircle size='16' />
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }
  if (!registrationQuery?.data || !groupRegistrationQuery?.data) { // registration is null
    return (
      <RegisterLayout showShoppingCart={false}>
        <BaseMessageBarError borderRadius={uiStyles.borderRadius}>This page does not exist.</BaseMessageBarError>
        <UiHStack justifyContent={'flex-end'} pt={8}>
          <UiButton colorScheme={'primary'} onClick={onBackEmail}>
            Back to register page
          </UiButton>
        </UiHStack>
      </RegisterLayout>
    );
  }
};

export default PrepareDetails;
